import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "permission-request-company-drawer__close-trigger" }
const _hoisted_2 = { class: "application-for-residency__section" }
const _hoisted_3 = { class: "application-for-residency__section-content" }
const _hoisted_4 = { class: "application-for-residency__section" }
const _hoisted_5 = { class: "application-for-residency__section-content" }
const _hoisted_6 = { class: "application-for-residency__section" }
const _hoisted_7 = { class: "application-for-residency__section-content" }
const _hoisted_8 = { class: "application-for-residency__footer" }
const _hoisted_9 = { class: "application-for-residency__buttons-container" }
const _hoisted_10 = ["disabled"]

import { computed, nextTick, reactive, ref, watch, onMounted } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import { type FormSubjectOption } from '@/js/components/form/form-subject-field.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import FormPrivacyConsentField from '@/js/components/form/form-privacy-consent-field.vue';
import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

interface ResydenceRequestFormMetaItem {
  id: string;
  text: string;
}

interface ResydenceRequestFormMetaResponse {
  status?: string;
  msg?: {
    clusters?: ResydenceRequestFormMetaItem[];
    business_area?: ResydenceRequestFormMetaItem[];
    privacy?: ResydenceRequestFormMetaItem[];
  };
}

interface ResidencyOpenParams {
  cluster?: string;
}

const PROJECT_DESCRIPTION_MAX_LENGTH = 512;


export default /*@__PURE__*/_defineComponent({
  __name: 'application-for-residency',
  setup(__props, { expose: __expose }) {

const RESIDENCE_BECOME_ENDPOINT = getApiUrl('/api/form/become-resident/');
function mapMetaToOptions(items: ResydenceRequestFormMetaItem[] | undefined): FormSubjectOption[] {
  return (items ?? []).map((item) => ({ value: item.id, label: item.text }));
}

const open = ref(false);
const openModal = ref(false);
const successModalDescription = ref('');
const isSubmitting = ref(false);
const generalFormRef = ref<FormInstance>();
const contactFormRef = ref<FormInstance>();
const documentsUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const documentItems = ref<FileUploadItem[]>([]);

const clusterOptions = ref<FormSubjectOption[]>([]);
const businessAreaOptions = ref<FormSubjectOption[]>([]);
const privacyOptionId = ref<string>('');

const initialFormState = () => ({
  full_name: '',
  business_area: '',
  other_business: '',
  cluster: '',
  project_description: '',
  contactFullName: '',
  phone: '',
  email: '',
  privacy: false,
  work_position: '',
});

const form = reactive(initialFormState());
const isOtherBusinessAreaSelected = computed(() => form.business_area === 'other');

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.full_name) &&
    isNotBlankString(form.business_area) &&
    (!isOtherBusinessAreaSelected.value || isNotBlankString(form.other_business)) &&
    isNotBlankString(form.cluster) &&
    isNotBlankString(form.project_description) &&
    isNotBlankString(form.contactFullName) &&
    isNotBlankString(form.phone) &&
    isNotBlankString(form.email) &&
    form.privacy,
);

const hasValidationErrors = computed(() => !isValidEmail(form.email));
const isSubmitDisabled = computed(
  () => isSubmitting.value || !isRequiredFieldsFilled.value || hasValidationErrors.value,
);

const rules: Record<string, Rule[]> = {
  full_name: [{ required: true, whitespace: true, message: 'Введите полное название' }],
  business_area: [{ required: true, whitespace: true, message: 'Выберите сферу деятельности' }],
  other_business: [{ required: true, whitespace: true, message: 'Укажите сферу деятельности' }],
  cluster: [{ required: true, whitespace: true, message: 'Выберите кластер' }],
  project_description: [
    { required: true, whitespace: true, message: 'Заполните описание проекта' },
  ],
  contactFullName: [{ required: true, whitespace: true, message: 'Введите ФИО' }],
  phone: [{ required: true, whitespace: true, message: 'Введите телефон' }],
  email: [{ required: true, type: 'email', message: 'Введите корректный e-mail' }],
  work_position: [{ required: false, whitespace: true, message: 'Название должности' }],
};

const showModal = () => {
  openModal.value = true;
};

const openDrawer = (params?: ResidencyOpenParams): void => {
  resetFormState();

  if (params?.cluster) {
    const exists = clusterOptions.value.some((option) => option.value === params.cluster);

    if (exists) {
      form.cluster = params.cluster;
    }
  }

  open.value = true;
};

const fetchResydenceRequestFormMeta = async () => {
  try {
    const response = await fetch(RESIDENCE_BECOME_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
    });

    const result = (await response.json()) as ResydenceRequestFormMetaResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(result?.error ?? 'Не удалось загрузить данные формы');
    }

    const privacyList = result.msg?.privacy ?? [];

    clusterOptions.value = mapMetaToOptions(result.msg?.clusters);
    businessAreaOptions.value = mapMetaToOptions(result.msg?.business_area);
    privacyOptionId.value = privacyList[0]?.id ?? '';
  } catch {
    message.error('Не удалось загрузить данные формы');
  }
};

watch(
  () => form.business_area,
  (value) => {
    if (value !== 'other') {
      form.other_business = '';
      nextTick(() => {
        generalFormRef.value?.clearValidate(['other_business']);
      });
    }
  },
);

const resetFormState = () => {
  Object.assign(form, initialFormState());
  documentItems.value = [];
  documentsUploadRef.value?.reset();
  nextTick(() => {
    generalFormRef.value?.clearValidate();
    contactFormRef.value?.clearValidate();
  });
};

const parseContactNameParts = (fullName: string): [string, string, string] => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const lastName = parts[0] ?? '';
  const firstName = parts[1] ?? '';
  const middleName = parts.slice(2).join(' ');
  return [lastName, firstName, middleName];
};

const resolveBusinessAreaValue = (): string => {
  if (form.business_area === 'other') {
    return form.other_business.trim();
  }

  return form.business_area;
};

const onClose = () => {
  open.value = false;
  resetFormState();
};

const onSubmit = async () => {
  if (!generalFormRef.value || !contactFormRef.value) {
    return;
  }

  try {
    await Promise.all([generalFormRef.value.validate(), contactFormRef.value.validate()]);
  } catch {
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const readyFiles = documentsUploadRef.value?.getReadyFiles() ?? [];

    const submitResult = await submitFormWithCaptcha({
      endpoint: RESIDENCE_BECOME_ENDPOINT,
      buildFormData: () => {
        const body = new FormData();
        const [lastName, firstName, middleName] = parseContactNameParts(form.contactFullName);

        body.append('full_name', form.full_name.trim());
        body.append('business_area', resolveBusinessAreaValue());
        body.append('cluster', form.cluster);
        body.append('last_name', lastName);
        body.append('first_name', firstName);
        body.append('middle_name', middleName);
        body.append('email', form.email.trim());
        body.append('phone', form.phone.trim());
        body.append('project_description', form.project_description.trim());

        if (form.work_position) {
          body.append('work_position', form.work_position);
        }

        if (form.privacy && privacyOptionId.value) {
          body.append('privacy', privacyOptionId.value);
        }

        readyFiles.forEach((file) => {
          body.append('documents[]', file);
        });

        return body;
      },
    });

    if (!submitResult.ok || !submitResult.data) {
      if (submitResult.errorCode === 'captcha-not-loaded') {
        message.error(getRecaptchaErrorMessage('captcha-not-loaded'));
        return;
      }

      if (submitResult.errorCode === 'captcha-failed') {
        message.error(getRecaptchaErrorMessage('captcha-failed'));
        return;
      }

      message.error(submitResult.message ?? 'Не удалось отправить заявку');
      return;
    }

    successModalDescription.value = submitResult.data.msg ?? '';
    trackYaGoal('submitApplication');
    onClose();
    showModal();
  } catch {
    message.error('Ошибка соединения');
  } finally {
    isSubmitting.value = false;
    documentsUploadRef.value?.resetSubmittingState();
  }
};

onMounted(() => {
  void fetchResydenceRequestFormMeta();
});

__expose({
  open: openDrawer,
});

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_drawer, {
      open: open.value,
      "root-class-name": "application-for-residency",
      class: "application-for-residency",
      onClose: onClose
    }, {
      title: _withCtx(() => [...(_cache[12] || (_cache[12] = [
        _createElementVNode("span", { class: "application-for-residency__header-title" }, "Заявка на резидентство", -1)
      ]))]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_1, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(FormPrivacyConsentField, {
            "model-value": form.privacy,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.privacy) = $event)),
            class: "policy-footer-mobile"
          }, null, 8, ["model-value"]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-outline btn-medium btn-cancel",
              onClick: onClose
            }, " Отмена "),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary btn-medium",
              onClick: onSubmit,
              disabled: isSubmitDisabled.value
            }, _toDisplayString(isSubmitting.value ? 'Отправка…' : 'Отправить'), 9, _hoisted_10)
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _cache[13] || (_cache[13] = _createElementVNode("span", { class: "application-for-residency__section-title" }, "1. Общее", -1)),
          _createVNode(_component_a_form, {
            ref_key: "generalFormRef",
            ref: generalFormRef,
            model: form,
            rules: rules,
            layout: "vertical",
            class: "application-for-residency__form"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Полное название юр. лица или ИП",
                  name: "full_name",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.full_name,
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.full_name) = $event)),
                      placeholder: "Введите название"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Сфера деятельности компании",
                  name: "business_area",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: form.business_area,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.business_area) = $event)),
                      placeholder: "Выберите сферу"
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(businessAreaOptions.value, (option) => {
                          return (_openBlock(), _createBlock(_component_a_select_option, {
                            key: option.value,
                            value: option.value
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(option.label), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Кластер",
                  name: "cluster",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: form.cluster,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.cluster) = $event)),
                      placeholder: "Выберите кластер"
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(clusterOptions.value, (option) => {
                          return (_openBlock(), _createBlock(_component_a_select_option, {
                            key: option.value,
                            value: option.value
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(option.label), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Описание проекта",
                  name: "project_description",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_textarea, {
                      value: form.project_description,
                      "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((form.project_description) = $event)),
                      class: "permission-request-company-drawer__textarea",
                      "auto-size": { minRows: 8, maxRows: 8 },
                      maxlength: PROJECT_DESCRIPTION_MAX_LENGTH,
                      placeholder: "Тип проекта, ожидаемые результаты",
                      "show-count": ""
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }, 8, ["model"])
        ]),
        _createVNode(_component_a_divider, { class: "application-for-residency__divider" }),
        _createElementVNode("div", _hoisted_4, [
          _cache[14] || (_cache[14] = _createElementVNode("span", { class: "application-for-residency__section-title" }, "2. Контактное лицо", -1)),
          _createVNode(_component_a_form, {
            ref_key: "contactFormRef",
            ref: contactFormRef,
            model: form,
            rules: rules,
            layout: "vertical",
            class: "application-for-residency__form"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "ФИО",
                  name: "contactFullName",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.contactFullName,
                      "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((form.contactFullName) = $event)),
                      placeholder: "Введите фамилию, имя и отчество"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Должность",
                  name: "work_position"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.work_position,
                      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((form.work_position) = $event)),
                      placeholder: "Название должности"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Телефон",
                  name: "phone",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.phone,
                      "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((form.phone) = $event)),
                      placeholder: "+7 (___) ___-__-__"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Почта",
                  name: "email",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.email,
                      "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((form.email) = $event)),
                      placeholder: "example@mail.ru"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }, 8, ["model"])
        ]),
        _createVNode(_component_a_divider, { class: "application-for-residency__divider" }),
        _createElementVNode("div", _hoisted_6, [
          _cache[15] || (_cache[15] = _createElementVNode("span", { class: "application-for-residency__section-title" }, "3. Документы", -1)),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(FormFileUpload, {
              ref_key: "documentsUploadRef",
              ref: documentsUploadRef,
              modelValue: documentItems.value,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((documentItems).value = $event))
            }, null, 8, ["modelValue"])
          ])
        ]),
        _createVNode(_component_a_divider, { class: "application-for-residency__divider-mobile" }),
        _createVNode(FormPrivacyConsentField, {
          "model-value": form.privacy,
          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.privacy) = $event)),
          class: "policy-mobile"
        }, null, 8, ["model-value"])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(ConsultationRequestSuccessModal, {
      open: openModal.value,
      "onUpdate:open": _cache[11] || (_cache[11] = ($event: any) => ((openModal).value = $event)),
      description: successModalDescription.value
    }, null, 8, ["open", "description"])
  ], 64))
}
}

})